/**
 * @file Reload-loop guard utilities.
 *
 * Prevents infinite page-reload loops when the refresh endpoint fails
 * irrecoverably. A sessionStorage flag is set before reloading; on the next
 * page load the interceptor checks the flag and skips the refresh attempt.
 */

import { isNullish } from '@/utils/null-utils';
import { TOKEN_REFRESH_CONFIG } from '../config';

const { reloadFlagKey: RELOAD_FLAG_KEY } = TOKEN_REFRESH_CONFIG;

/**
 * Returns `true` if the page just reloaded due to a failed refresh.
 * @returns Whether the reload-loop guard flag is set.
 */
export function hasReloadFlag(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  return !isNullish(sessionStorage.getItem(RELOAD_FLAG_KEY));
}

/** Clears the reload flag so subsequent requests proceed normally. */
export function clearReloadFlag(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(RELOAD_FLAG_KEY);
  }
}

/**
 * Sets the reload flag before a guarded page reload.
 * @remarks Called by `core.ts` when `doRefresh` fails.
 */
export function setReloadFlag(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(RELOAD_FLAG_KEY, '1');
  }
}
